import type { Asset } from '@shared/types';
import { fetchWithRetry } from '@utils/fetchWithRetry';
import { getCurrentTime, getTimeshiftOffsetMs } from './timeshiftService';

export interface PriceData {
  price: number;
  timestamp: number;
}

export interface SecondPricePoint {
  price: number;
  timestamp: number; // ms
  tradeId?: number;
}

interface CandleData {
  symbol: 'BTC' | 'ETH' | 'SOL' | 'XRP';
  timeframe: '1s' | '5s' | '15s' | '30s' | '1m' | '5m' | '10m' | '15m';
  openTime: string;
  closeTime: string;
  open: string;
  high: string;
  low: string;
  close: string;
}

interface CandlesResponse {
  symbol: 'BTC' | 'ETH' | 'SOL' | 'XRP';
  timeframe: '1s' | '5s' | '15s' | '30s' | '1m' | '5m' | '10m' | '15m';
  data: CandleData[];
  pagination: {
    total: number;
    limit: number;
    hasNext: boolean;
    nextCursor?: string;
  };
}

export class PriceService {
  private ws: WebSocket | null = null;
  private subscribers: Set<(prices: Map<Asset, PriceData>) => void> = new Set();
  private prices: Map<Asset, PriceData> = new Map();
  private realtimePricesBySymbol: Map<Asset, SecondPricePoint[]> = new Map();
  private bootstrappedSymbols: Set<Asset> = new Set();

  // Simulation time tracking - increments by 1s per update to simulate historical price evolution
  private simulationTimeBySymbol: Map<Asset, number> = new Map();

  // Connection state management
  private lastNotificationTime = 0;
  private readonly NOTIFICATION_THROTTLE_MS = 16; // ~60fps max
  private isWebSocketConnected = false;
  private lastMessageTime = 0;

  private supportedSymbols = ['BTC', 'ETH', 'SOL', 'XRP'] as const;

  private readonly API_BASE_URL = 'https://api-production-9b1a.up.railway.app';
  private readonly WS_URL = 'wss://api-production-9b1a.up.railway.app/ws';

  // Public method to fetch candles for other hooks
  public async fetchCandles(
    symbol: Asset,
    timeframe: '1s' | '5s' | '15s' | '30s' | '1m' | '5m' | '10m' | '15m',
    limit?: number,
    start?: string,
    end?: string,
  ): Promise<CandleData[]> {
    const url = new URL(`${this.API_BASE_URL}/api/v1/candles`);
    url.searchParams.set('symbol', symbol);
    url.searchParams.set('timeframe', timeframe);

    if (limit) {
      url.searchParams.set('limit', limit.toString());
    }
    if (start) {
      url.searchParams.set('start', start);
    }
    if (end) {
      url.searchParams.set('end', end);
    }

    try {
      const response = await fetchWithRetry(url.toString());
      if (!response.ok) {
        throw new Error(`Price API HTTP ${response.status}`);
      }

      const data: CandlesResponse = await response.json();
      return data.data;
    } catch (error) {
      console.warn(
        `[PriceService] Failed to fetch ${timeframe} candles for ${symbol}:`,
        error,
      );
      return [];
    }
  }

  async bootstrap(symbolsToLoad?: Asset[]) {
    const symbols = symbolsToLoad ?? this.supportedSymbols;

    for (const symbol of symbols) {
      if (this.bootstrappedSymbols.has(symbol)) {
        continue;
      }

      try {
        console.log(`[PriceService] Bootstrapping ${symbol}...`);

        // Fetch 30 minutes total:
        // - Last 10 minutes: 1s candles (600 candles)
        // - Previous 20 minutes: 5s candles (240 candles)
        const now = getCurrentTime();
        const tenMinutesAgo = new Date(now - 10 * 60 * 1000);
        const thirtyMinutesAgo = new Date(now - 30 * 60 * 1000);

        // Fetch 1s candles for last 10 minutes
        const recent1sCandles = await this.fetchCandles(
          symbol,
          '1s',
          600,
          tenMinutesAgo.toISOString(),
          new Date(now).toISOString(),
        );

        // Fetch 5s candles for 20 minutes before that (10-30 minutes ago)
        const older5sCandles = await this.fetchCandles(
          symbol,
          '5s',
          240, // 20 minutes * 12 candles per minute
          thirtyMinutesAgo.toISOString(),
          tenMinutesAgo.toISOString(),
        );

        // Convert both to SecondPricePoint format and combine
        // Timeshift the timestamps to align with our simulation timeline
        const timeshiftOffset = getTimeshiftOffsetMs();
        const recent1sPoints = recent1sCandles.map(candle => ({
          price: parseFloat(candle.close),
          timestamp: new Date(candle.openTime).getTime() + timeshiftOffset,
        }));

        const older5sPoints = older5sCandles.map(candle => ({
          price: parseFloat(candle.close),
          timestamp: new Date(candle.openTime).getTime() + timeshiftOffset,
        }));

        // Combine and sort all candles by timestamp
        const candles = [...older5sPoints, ...recent1sPoints].sort(
          (a, b) => a.timestamp - b.timestamp,
        );

        // Store in memory
        this.realtimePricesBySymbol.set(symbol, candles);

        // Initialize simulation time to the earliest timestamp in bootstrap data
        // This will be the starting point for simulating historical price evolution
        if (candles.length > 0) {
          const earliest = candles[0];
          this.simulationTimeBySymbol.set(symbol, earliest.timestamp);
          
          // Update current price with latest candle
          const latest = candles[candles.length - 1];
          this.prices.set(symbol, {
            price: latest.price,
            timestamp: latest.timestamp,
          });
        } else {
          // If no candles, initialize simulation time to current timeshifted time
          this.simulationTimeBySymbol.set(symbol, getCurrentTime());
        }

        this.bootstrappedSymbols.add(symbol);
        console.log(
          `[PriceService] ${symbol} bootstrap complete: ${older5sCandles.length} 5s candles + ${recent1sCandles.length} 1s candles = ${candles.length} total`,
        );
      } catch (error) {
        console.warn(`[PriceService] Bootstrap failed for ${symbol}:`, error);
        this.realtimePricesBySymbol.set(symbol, []);
        // Initialize simulation time to current timeshifted time if bootstrap fails
        this.simulationTimeBySymbol.set(symbol, getCurrentTime());
        this.bootstrappedSymbols.add(symbol);
      }
    }

    // Start WebSocket for real-time updates (only once)
    this.ensureWebSocketConnected();

    // Notify subscribers
    this.notifySubscribers();
  }

  public isSymbolBootstrapped(symbol: Asset) {
    return this.bootstrappedSymbols.has(symbol);
  }

  async ensureSymbolBootstrapped(symbol: Asset) {
    if (
      !this.bootstrappedSymbols.has(symbol) &&
      this.supportedSymbols.includes(symbol)
    ) {
      await this.bootstrap([symbol]);
    }
  }

  private ensureWebSocketConnected() {
    // Check if connection is truly alive (received message in last 60 seconds)
    const now = Date.now();
    const connectionTimeout = 60_000; // 60 seconds
    const isConnectionStale =
      this.lastMessageTime > 0 &&
      now - this.lastMessageTime > connectionTimeout;

    if (isConnectionStale) {
      console.log(
        '[PriceService] WebSocket connection appears stale, forcing reconnection...',
      );
      this.forceReconnect();
      return;
    }

    if (this.isWebSocketConnected) {
      console.log(
        '[PriceService] WebSocket already connected, skipping connection attempt',
      );
      return; // Already connected, don't create another connection
    }

    if (
      this.ws &&
      (this.ws.readyState === WebSocket.CONNECTING ||
        this.ws.readyState === WebSocket.OPEN)
    ) {
      console.log(
        '[PriceService] WebSocket connection in progress or already open, skipping',
      );
      return;
    }

    console.log('[PriceService] Ensuring WebSocket connection...');
    this.connect();
  }

  private forceReconnect() {
    console.log('[PriceService] Forcing WebSocket reconnection...');
    this.isWebSocketConnected = false;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.connect();
  }

  private connect() {
    // Prevent multiple connections more aggressively
    if (
      this.isWebSocketConnected ||
      (this.ws &&
        (this.ws.readyState === WebSocket.OPEN ||
          this.ws.readyState === WebSocket.CONNECTING))
    ) {
      console.log(
        '[PriceService] Connection already exists or in progress, skipping',
      );
      return;
    }

    console.log('[PriceService] Creating WebSocket connection...');
    this.ws = new WebSocket(this.WS_URL);

    this.ws.onopen = () => {
      console.log('[PriceService] WebSocket connected');

      // Add a small delay to ensure the connection is fully ready
      setTimeout(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.isWebSocketConnected = true;

          // Subscribe to 1s candles for all supported symbols
          const subscribeMsg = {
            type: 'subscribe',
            channels: this.supportedSymbols.map(symbol => ({
              symbol,
              timeframe: '1s' as const,
            })),
          };

          try {
            this.ws.send(JSON.stringify(subscribeMsg));
            console.log('[PriceService] Subscribed to all symbols');
          } catch (error) {
            console.error('[PriceService] Failed to send subscription:', error);
            this.isWebSocketConnected = false;
          }
        }
      }, 100);
    };

    this.ws.onmessage = ev => {
      try {
        // Track that we received a message (connection is alive)
        this.lastMessageTime = Date.now();

        const msg = JSON.parse(ev.data);
        const type = msg?.type as string;

        if (type === 'connected') {
          console.log('[PriceService] Connection confirmed:', msg.connectionId);
        } else if (type === 'subscription_confirmed') {
          console.log('[PriceService] Subscription confirmed:', msg.channels);
        } else if (type === 'candle_update') {
          this.processCandleUpdate(msg);
        } else if (type === 'ping') {
          // Respond to server ping with pong
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type: 'pong' }));
          }
        } else if (type === 'error') {
          console.error(
            '[PriceService] WebSocket error:',
            msg.error,
            msg.message,
          );
        }
      } catch (e) {
        console.error('[PriceService] WS parse error', e);
      }
    };

    this.ws.onclose = () => {
      console.log('[PriceService] WebSocket disconnected, reconnecting...');
      this.isWebSocketConnected = false;
      this.ws = null;
      setTimeout(() => {
        if (!this.isWebSocketConnected) {
          // Only reconnect if we're not already connected
          this.connect();
        }
      }, 1000);
    };

    this.ws.onerror = error => {
      console.warn('[PriceService] WebSocket error:', error);
      this.isWebSocketConnected = false;
    };
  }

  private processCandleUpdate(msg: {
    symbol: Asset;
    timeframe: string;
    candle: {
      symbol: Asset;
      timeframe: string;
      openTime: string;
      closeTime: string;
      open: string;
      high: string;
      low: string;
      close: string;
    };
  }) {
    const { symbol, candle } = msg;

    // Validate symbol is supported
    if (!this.supportedSymbols.includes(symbol)) {
      return;
    }

    // Use simulation time that increments by 1 second per update
    // This simulates how prices evolved historically, advancing through time
    let simulationTime = this.simulationTimeBySymbol.get(symbol);
    if (simulationTime === undefined) {
      // Initialize simulation time to current timeshifted time if not set
      simulationTime = getCurrentTime();
      this.simulationTimeBySymbol.set(symbol, simulationTime);
    } else {
      // Increment simulation time by 1 second for each update
      simulationTime += 1000;
      this.simulationTimeBySymbol.set(symbol, simulationTime);
    }
    
    const sec = Math.floor(simulationTime / 1000) * 1000;

    // Look up the historical price at the simulation timestamp
    // This ensures we use the price that actually existed at that time, simulating historical evolution
    const history = this.realtimePricesBySymbol.get(symbol) ?? [];
    let price: number | null = null;

    // Look for price in existing history (check backwards from the simulation timestamp)
    // Bootstrap data should already contain historical prices for the timeshifted timeline
    for (let i = history.length - 1; i >= 0; i--) {
      const p = history[i];
      if (p.timestamp <= sec) {
        price = p.price;
        break;
      }
    }

    // If not found in existing history, fetch it asynchronously and push when ready
    // Do NOT use real-time price - only use historical prices
    if (price === null) {
      // Fetch historical price for the simulation timestamp
      this.fetchPriceAtTimestamp(symbol, sec).then(historicalPrice => {
        if (historicalPrice && historicalPrice.price > 0) {
          const updatedHistory = this.realtimePricesBySymbol.get(symbol) ?? [];
          const last = updatedHistory[updatedHistory.length - 1];

          // Add or update the price point with historical price
          if (history.length === 0 || !last || last.timestamp < sec) {
            updatedHistory.push({ price: historicalPrice.price, timestamp: sec });
            if (symbol === 'SOL') {
              console.log(
                `[PriceService] Appended price for ${symbol}: price=$${historicalPrice.price.toFixed(4)}, ` +
                `currentTime=${new Date(Date.now()).toISOString()}, ` +
                `priceTime=${new Date(sec).toISOString()}`,
              );
            }
          } else if (last.timestamp === sec) {
            last.price = historicalPrice.price;
            if (symbol === 'SOL') {
              console.log(
                `[PriceService] Updated price for ${symbol}: price=$${historicalPrice.price.toFixed(4)}, ` +
                `currentTime=${new Date(Date.now()).toISOString()}, ` +
                `priceTime=${new Date(sec).toISOString()}`,
              );
            }
          } else {
            updatedHistory.push({ price: historicalPrice.price, timestamp: sec });
            if (symbol === 'SOL') {
              console.log(
                `[PriceService] Appended price for ${symbol}: price=$${historicalPrice.price.toFixed(4)}, ` +
                `currentTime=${new Date(Date.now()).toISOString()}, ` +
                `priceTime=${new Date(sec).toISOString()}`,
              );
            }
          }

          this.realtimePricesBySymbol.set(symbol, updatedHistory);

          // Update last price with historical price
          this.prices.set(symbol, {
            price: historicalPrice.price,
            timestamp: sec,
          });

          this.notifySubscribers();
        }
      }).catch(() => {
        // Ignore fetch errors - skip this update if we can't get historical price
      });
      
      // Skip this update - don't push real-time price, wait for historical fetch
      return;
    }

    if (!price || !(price > 0) || !Number.isFinite(price)) {
      return;
    }

    // Update last price with simulation timestamp and historical price
    this.prices.set(symbol, {
      price,
      timestamp: sec,
    });

    // Update history per second bucket
    const last = history[history.length - 1];

    if (history.length === 0 || !last || last.timestamp < sec) {
      history.push({ price, timestamp: sec });
      if (symbol === 'SOL') {
        console.log(
          `[PriceService] Appended price for ${symbol}: price=$${price.toFixed(4)}, ` +
          `currentTime=${new Date(Date.now()).toISOString()}, ` +
          `priceTime=${new Date(sec).toISOString()}`,
        );
      }
    } else if (last.timestamp === sec) {
      last.price = price;
      if (symbol === 'SOL') {
        console.log(
          `[PriceService] Updated price for ${symbol}: price=$${price.toFixed(4)}, ` +
          `currentTime=${new Date(Date.now()).toISOString()}, ` +
          `priceTime=${new Date(sec).toISOString()}`,
        );
      }
    } else {
      history.push({ price, timestamp: sec });
      if (symbol === 'SOL') {
        console.log(
          `[PriceService] Appended price for ${symbol}: price=$${price.toFixed(4)}, ` +
          `currentTime=${new Date(Date.now()).toISOString()}, ` +
          `priceTime=${new Date(sec).toISOString()}`,
        );
      }
    }

    // Keep last 15 minutes only for real-time updates
    const cutoff = getCurrentTime() - 15 * 60_000;
    const cleanedHistory = history.filter(point => point.timestamp >= cutoff);
    this.realtimePricesBySymbol.set(symbol, cleanedHistory);

    this.notifySubscribers();
  }

  private notifySubscribers() {
    // Throttle notifications to prevent excessive re-renders
    const now = Date.now();
    if (now - this.lastNotificationTime < this.NOTIFICATION_THROTTLE_MS) {
      return;
    }
    this.lastNotificationTime = now;

    const snapshot = new Map(this.prices);
    this.subscribers.forEach(cb => {
      try {
        cb(snapshot);
      } catch {
        // noop
      }
    });
  }

  /**
   * Get current connection state
   */
  public getConnectionState() {
    return {
      isConnected:
        this.isWebSocketConnected && this.ws?.readyState === WebSocket.OPEN,
      connectionCount: this.isWebSocketConnected ? 1 : 0,
    };
  }

  public subscribe(callback: (prices: Map<Asset, PriceData>) => void) {
    this.subscribers.add(callback);
    callback(new Map(this.prices));
    return () => this.subscribers.delete(callback);
  }

  public getPrice(symbol: Asset) {
    return this.prices.get(symbol);
  }

  public getPricesSnapshot(): Map<Asset, PriceData> {
    return new Map(this.prices);
  }

  public getRealtimePrices(symbol: Asset): SecondPricePoint[] {
    return this.realtimePricesBySymbol.get(symbol) ?? [];
  }

  public async fetchPriceAtTimestamp(
    symbol: Asset,
    timestampMs: number,
  ): Promise<SecondPricePoint | null> {
    const history = this.realtimePricesBySymbol.get(symbol) ?? [];
    if (history.length > 0) {
      for (let i = history.length - 1; i >= 0; i--) {
        const p = history[i];
        if (p.timestamp <= timestampMs) {
          return p;
        }
      }
    }

    // Fallback: try to fetch from our API
    // The timestampMs parameter is a simulation timestamp (already timeshifted)
    // We need to convert it back to real time for the API, then timeshift the result
    try {
      const timeshiftOffset = getTimeshiftOffsetMs();
      const realTimestamp = timestampMs - timeshiftOffset;
      const startTime = new Date(realTimestamp - 1000); // 1 second before
      const endTime = new Date(realTimestamp + 1000); // 1 second after
      
      const rawCandles = await this.fetchCandles(
        symbol,
        '1s',
        2,
        startTime.toISOString(),
        endTime.toISOString(),
      );
      
      if (rawCandles.length > 0) {
        // Find the candle closest to our target timestamp
        let bestCandle = rawCandles[0];
        let bestDiff = Math.abs(new Date(bestCandle.openTime).getTime() - realTimestamp);
        
        for (const candle of rawCandles) {
          const candleTime = new Date(candle.openTime).getTime();
          const diff = Math.abs(candleTime - realTimestamp);
          if (diff < bestDiff) {
            bestDiff = diff;
            bestCandle = candle;
          }
        }
        
        // Timeshift the timestamp back to simulation time
        return {
          price: parseFloat(bestCandle.close),
          timestamp: new Date(bestCandle.openTime).getTime() + timeshiftOffset,
        };
      }
      return null;
    } catch {
      return null;
    }
  }
}

export const priceService = new PriceService();
